import React, { useState, useEffect } from 'react'
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  BriefcaseIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  ClockIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  PlayIcon,
  PauseIcon,
  StopIcon
} from '@heroicons/react/24/outline'
import { projectsAPI, type Project, type ProjectStats } from '@/services/api'

// Interface is now imported from API

const ProjectsPage: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [filterStatus, setFilterStatus] = useState<'ALL' | 'planning' | 'active' | 'on_hold' | 'completed' | 'cancelled'>('ALL')

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true)
        // Mock data - to be replaced with actual API call
        setProjects([
          {
            id: 1,
            name: 'Sistema de Vendas Online',
            description: 'Desenvolvimento de plataforma de e-commerce para expansão digital',
            status: 'active',
            manager_id: 1,
            manager_name: 'João Silva',
            start_date: '2024-01-15',
            end_date: '2024-06-30',
            budget: 150000,
            spent: 85000,
            progress: 65,
            cost_center_id: 1,
            cost_center_name: 'TI',
            priority: 'high',
            team_size: 8,
            created_at: '2024-01-10T00:00:00Z',
            updated_at: '2024-01-20T00:00:00Z'
          },
          {
            id: 2,
            name: 'Migração para Cloud',
            description: 'Migração da infraestrutura local para AWS',
            status: 'planning',
            manager_id: 2,
            manager_name: 'Maria Santos',
            start_date: '2024-03-01',
            budget: 200000,
            spent: 0,
            progress: 0,
            cost_center_id: 1,
            cost_center_name: 'TI',
            priority: 'critical',
            team_size: 5,
            created_at: '2024-02-01T00:00:00Z',
            updated_at: '2024-02-15T00:00:00Z'
          },
          {
            id: 3,
            name: 'Campanha Marketing Digital',
            description: 'Estratégia de marketing digital para Q2 2024',
            status: 'active',
            manager_id: 3,
            manager_name: 'Pedro Costa',
            start_date: '2024-02-01',
            end_date: '2024-05-31',
            budget: 80000,
            spent: 32000,
            progress: 40,
            cost_center_id: 2,
            cost_center_name: 'Marketing',
            priority: 'medium',
            team_size: 4,
            created_at: '2024-01-25T00:00:00Z',
            updated_at: '2024-02-10T00:00:00Z'
          },
          {
            id: 4,
            name: 'Treinamento Equipe Vendas',
            description: 'Programa de capacitação para equipe de vendas',
            status: 'completed',
            manager_id: 4,
            manager_name: 'Ana Oliveira',
            start_date: '2023-11-01',
            end_date: '2023-12-31',
            budget: 25000,
            spent: 23500,
            progress: 100,
            cost_center_id: 3,
            cost_center_name: 'Vendas',
            priority: 'medium',
            team_size: 12,
            created_at: '2023-10-15T00:00:00Z',
            updated_at: '2024-01-05T00:00:00Z'
          }
        ])
      } catch (err: any) {
        setError(err.message || 'Erro ao carregar projetos')
      } finally {
        setLoading(false)
      }
    }

    fetchProjects()
  }, [])

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'planning':
        return 'bg-blue-100 text-blue-800'
      case 'active':
        return 'bg-green-100 text-green-800'
      case 'on_hold':
        return 'bg-yellow-100 text-yellow-800'
      case 'completed':
        return 'bg-gray-100 text-gray-800'
      case 'cancelled':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'planning':
        return 'Planejamento'
      case 'active':
        return 'Ativo'
      case 'on_hold':
        return 'Pausado'
      case 'completed':
        return 'Concluído'
      case 'cancelled':
        return 'Cancelado'
      default:
        return status
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'planning':
        return <InformationCircleIcon className="w-4 h-4" />
      case 'active':
        return <PlayIcon className="w-4 h-4" />
      case 'on_hold':
        return <PauseIcon className="w-4 h-4" />
      case 'completed':
        return <CheckCircleIcon className="w-4 h-4" />
      case 'cancelled':
        return <StopIcon className="w-4 h-4" />
      default:
        return <InformationCircleIcon className="w-4 h-4" />
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'low':
        return 'text-gray-500'
      case 'medium':
        return 'text-blue-500'
      case 'high':
        return 'text-orange-500'
      case 'critical':
        return 'text-red-500'
      default:
        return 'text-gray-500'
    }
  }

  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case 'low':
        return 'Baixa'
      case 'medium':
        return 'Média'
      case 'high':
        return 'Alta'
      case 'critical':
        return 'Crítica'
      default:
        return priority
    }
  }

  const filteredProjects = projects.filter(project => {
    if (filterStatus === 'ALL') return true
    return project.status === filterStatus
  })

  const activeProjects = projects.filter(p => p.status === 'active')
  const completedProjects = projects.filter(p => p.status === 'completed')
  const totalBudget = projects.reduce((sum, p) => sum + p.budget, 0)
  const totalSpent = projects.reduce((sum, p) => sum + p.spent, 0)

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded mb-4"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-48 bg-gray-100 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
          <h3 className="text-red-800 font-semibold mb-2">Erro ao carregar projetos</h3>
          <p className="text-red-600">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Projetos</h1>
          <p className="text-gray-600">
            {projects.length} projetos • {activeProjects.length} ativos • {completedProjects.length} concluídos
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-lg hover:from-blue-700 hover:to-purple-700 flex items-center gap-2 transition-all duration-200 shadow-lg hover:shadow-xl"
        >
          <PlusIcon className="w-5 h-5" />
          Novo Projeto
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Total de Projetos</p>
              <p className="text-2xl font-bold">{projects.length}</p>
            </div>
            <BriefcaseIcon className="w-8 h-8 text-blue-200" />
          </div>
        </div>
        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Projetos Ativos</p>
              <p className="text-2xl font-bold">{activeProjects.length}</p>
            </div>
            <PlayIcon className="w-8 h-8 text-green-200" />
          </div>
        </div>
        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">Orçamento Total</p>
              <p className="text-xl font-bold">{formatCurrency(totalBudget)}</p>
            </div>
            <CurrencyDollarIcon className="w-8 h-8 text-purple-200" />
          </div>
        </div>
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm">Total Gasto</p>
              <p className="text-xl font-bold">{formatCurrency(totalSpent)}</p>
            </div>
            <ChartBarIcon className="w-8 h-8 text-orange-200" />
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="bg-white rounded-xl border border-gray-200 p-1 shadow-sm">
        <div className="flex space-x-1 overflow-x-auto">
          {[
            { key: 'ALL', label: 'Todos', count: projects.length },
            { key: 'planning', label: 'Planejamento', count: projects.filter(p => p.status === 'planning').length },
            { key: 'active', label: 'Ativos', count: activeProjects.length },
            { key: 'on_hold', label: 'Pausados', count: projects.filter(p => p.status === 'on_hold').length },
            { key: 'completed', label: 'Concluídos', count: completedProjects.length },
            { key: 'cancelled', label: 'Cancelados', count: projects.filter(p => p.status === 'cancelled').length }
          ].map((filter) => (
            <button
              key={filter.key}
              onClick={() => setFilterStatus(filter.key as any)}
              className={`flex-shrink-0 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                filterStatus === filter.key
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {filter.label} ({filter.count})
            </button>
          ))}
        </div>
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredProjects.map((project) => (
          <div key={project.id} className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-semibold text-gray-900 text-lg">{project.name}</h3>
                  <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(project.status)}`}>
                    {getStatusIcon(project.status)}
                    {getStatusLabel(project.status)}
                  </span>
                </div>
                <p className="text-gray-600 text-sm line-clamp-2 mb-3">{project.description}</p>
                <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                  <div className="flex items-center gap-1">
                    <UserGroupIcon className="w-4 h-4" />
                    {project.manager_name}
                  </div>
                  <div className={`flex items-center gap-1 font-medium ${getPriorityColor(project.priority)}`}>
                    <ExclamationTriangleIcon className="w-4 h-4" />
                    {getPriorityLabel(project.priority)}
                  </div>
                </div>
              </div>
              <div className="flex gap-1 ml-4">
                <button className="text-blue-600 hover:text-blue-900 p-1">
                  <PencilIcon className="w-4 h-4" />
                </button>
                <button className="text-red-600 hover:text-red-900 p-1">
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mb-4">
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>Progresso</span>
                <span>{project.progress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${project.progress}%` }}
                ></div>
              </div>
            </div>

            {/* Budget Information */}
            <div className="space-y-2 mb-4">
              <div className="flex justify-between">
                <span className="text-gray-600 text-sm">Orçamento:</span>
                <span className="font-medium">{formatCurrency(project.budget)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 text-sm">Gasto:</span>
                <span className={`font-medium ${project.spent > project.budget ? 'text-red-600' : 'text-green-600'}`}>
                  {formatCurrency(project.spent)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 text-sm">Disponível:</span>
                <span className={`font-medium ${(project.budget - project.spent) < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                  {formatCurrency(project.budget - project.spent)}
                </span>
              </div>
            </div>

            {/* Additional Info */}
            <div className="space-y-2 pt-4 border-t border-gray-100">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Centro de Custo:</span>
                <span className="text-gray-900">{project.cost_center_name || 'N/A'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Equipe:</span>
                <span className="text-gray-900">{project.team_size} pessoas</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Início:</span>
                <span className="text-gray-900">
                  {new Date(project.start_date).toLocaleDateString('pt-BR')}
                </span>
              </div>
              {project.end_date && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Fim:</span>
                  <span className="text-gray-900">
                    {new Date(project.end_date).toLocaleDateString('pt-BR')}
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {filteredProjects.length === 0 && (
        <div className="text-center py-12">
          <BriefcaseIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {filterStatus === 'ALL'
              ? 'Nenhum projeto encontrado'
              : `Nenhum projeto ${getStatusLabel(filterStatus).toLowerCase()} encontrado`
            }
          </h3>
          <p className="text-gray-500 mb-6">
            {filterStatus === 'ALL'
              ? 'Comece criando seu primeiro projeto para gerenciar atividades e orçamentos'
              : `Não há projetos com status ${getStatusLabel(filterStatus).toLowerCase()} no momento`
            }
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-purple-700 flex items-center gap-2 mx-auto transition-all duration-200"
          >
            <PlusIcon className="w-5 h-5" />
            Criar Primeiro Projeto
          </button>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Novo Projeto</h3>
            <form className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome do Projeto
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: Sistema de Vendas Online"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descrição
                  </label>
                  <textarea
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    placeholder="Descrição detalhada do projeto..."
                  ></textarea>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="planning">Planejamento</option>
                    <option value="active">Ativo</option>
                    <option value="on_hold">Pausado</option>
                    <option value="completed">Concluído</option>
                    <option value="cancelled">Cancelado</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prioridade
                  </label>
                  <select className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="low">Baixa</option>
                    <option value="medium">Média</option>
                    <option value="high">Alta</option>
                    <option value="critical">Crítica</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Gerente do Projeto
                  </label>
                  <select className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">Selecione um gerente</option>
                    <option value="1">João Silva</option>
                    <option value="2">Maria Santos</option>
                    <option value="3">Pedro Costa</option>
                    <option value="4">Ana Oliveira</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Centro de Custo
                  </label>
                  <select className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">Selecione um centro de custo</option>
                    <option value="1">TI</option>
                    <option value="2">Marketing</option>
                    <option value="3">Vendas</option>
                    <option value="4">Administrativo</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data de Início
                  </label>
                  <input
                    type="date"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data de Fim (opcional)
                  </label>
                  <input
                    type="date"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Orçamento (R$)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0,00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tamanho da Equipe
                  </label>
                  <input
                    type="number"
                    min="1"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="1"
                  />
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Criar Projeto
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProjectsPage